import { Localized, useLocalization } from "@fluent/react";
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Stack,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useAppDispatch } from "../../../app/hooks";
import { AppId, AppTextFieldHandler } from "../../../app/appTypes";
import { createDocumentNewVersion } from "../documentsApi";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { useNavigate } from "react-router-dom";

export interface DocumentNewVersionDialogProps {
    documentId: AppId;
    onSuccess: (documentId: AppId) => void;
    onCancel: () => void;
}

export const DocumentNewVersionDialog = ({
    documentId,
    onSuccess,
    onCancel,
}: DocumentNewVersionDialogProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate()
    const { l10n } = useLocalization()

    const [changeReason, setChangeReason] = useState("");
    const [changeDescription, setChangeDescription] = useState("");
    const [password, setPassword] = useState("");
    const [running, setRunning] = useState(false);
    const [serverError, setServerError] = useState("");
    const [validationErrors, setValidationErrors] = useState(false);

    const passwordError = validationErrors && !password?.trim();
    const changeReasonError = validationErrors && !changeReason?.trim();
    const changeDescriptionError = validationErrors && !changeDescription?.trim();

    const createNewVersion = async () => {
        setValidationErrors(true);

        if (documentId && password && changeReason && changeDescription) {
            try {
                setRunning(true);

                const result = await dispatch(
                    createDocumentNewVersion({
                        documentId,
                        password,
                        changeReason,
                        changeDescription,
                    })
                );

                if (createDocumentNewVersion.fulfilled.match(result)) {
                    const newId = result.payload as AppId;
                    dispatch(showSuccess("sent"));
                    onSuccess(newId);
                    navigate(`/document/edit/${newId}`)
                } else {
                    let errorMsg = "error";
                    if ((result as any).payload?.kind === "http") {
                        const problem = (result as any).payload.problem;
                        if (problem?.title) errorMsg = problem.title;
                    }
                    setServerError(errorMsg);
                }
            } catch {
                dispatch(showError("error"));
            } finally {
                setRunning(false);
                setValidationErrors(false);
            }
        }
    };

    const handleChangeReasonFieldChange: AppTextFieldHandler = useCallback(
        (e) => setChangeReason(e.target.value),
        []
    );
    const handlePasswordFieldChange: AppTextFieldHandler = useCallback(
        (e) => setPassword(e.target.value),
        []
    );
    const handleChangeDescriptionFieldChange: AppTextFieldHandler = useCallback(
        (e) => setChangeDescription(e.target.value),
        []
    );

    return (
        <Dialog open maxWidth="sm" fullWidth>
            <DialogTitle
                sx={(t) => ({
                    bgcolor: t.palette.grey[100],
                    borderBottom: `1px solid ${t.palette.divider}`,
                    py: 1.5,
                    fontWeight: 700,
                })}
            >
                <Localized id="document-new-version">
                    <span>New document version</span>
                </Localized>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 2 }}>
                <Stack spacing={2}>
                    <Alert severity="warning" variant="outlined">
                        <Localized id="feature-under-development">
                            <span>_Sorry, this feature is under development and will be available soon.</span>
                        </Localized>
                    </Alert>

                    <TextField
                        fullWidth
                        size="medium"
                        margin="normal"
                        value={changeReason}
                        error={changeReasonError}
                        required
                        InputLabelProps={{ shrink: true }}
                        placeholder={l10n.getString(
                            "document-new-version-change-reason-placeholder",
                            null,
                            "For example: New version after rejection"
                        )}

                        onChange={handleChangeReasonFieldChange}
                        label={
                            <Localized id="change-reason">
                                <span>Reason for change</span>
                            </Localized>
                        }
                        helperText={
                            changeReasonError ? (
                                <Localized id="required">
                                    <span>Required</span>
                                </Localized>
                            ) : " "
                        }
                    />

                    <TextField
                        fullWidth
                        size="medium"
                        margin="normal"
                        multiline
                        minRows={3}
                        value={changeDescription}
                        error={changeDescriptionError}
                        required
                        InputLabelProps={{ shrink: true }}
                        onChange={handleChangeDescriptionFieldChange}
                        label={
                            <Localized id="change-description">
                                <span>Change description</span>
                            </Localized>
                        }
                        helperText={
                            changeDescriptionError ? (
                                <Localized id="required">
                                    <span>Required</span>
                                </Localized>
                            ) : " "
                        }
                    />

                    <TextField
                        fullWidth
                        size="medium"
                        margin="normal"
                        error={passwordError}
                        autoFocus
                        type="password"
                        required
                        InputLabelProps={{ shrink: true }}
                        value={password}
                        onChange={handlePasswordFieldChange}
                        label={
                            <Localized id="password">
                                <span>Password</span>
                            </Localized>
                        }
                        helperText={
                            passwordError ? (
                                <Localized id="autorization-password-required">
                                    <span>Password authorization is required</span>
                                </Localized>
                            ) : " "
                        }
                        autoComplete="off"
                    />

                    {serverError && (
                        <Alert
                            severity="error"
                            onClose={() => setServerError("")}
                            sx={{ mt: 1 }}
                        >
                            <Localized id={serverError}>
                                <span>Server error</span>
                            </Localized>
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <SaveButton disabled={true} loading={running} onClick={createNewVersion} />
                <CancelButton loading={running} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
